import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Predecessor, PREDECESSOR_TYPES } from '@tasks/tasks.types';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { TsButtonModule, TsInputComponent } from 'tsui';
import { AsyncPipe, NgIf } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TsSelectModule } from 'tsui/lib/ts-select/ts.select.module';
import { Task } from '@tasks/tasks.types';
import { map, Observable, of } from 'rxjs';
import { TasksService } from '@tasks/services/tasks.service';
import { TaskUtils } from '@tasks/tasks.utils';
import { TsSkeletonComponent } from 'tsui/lib/ts-skeleton/ts-skeleton.component';
import { DurationType } from '@shared/modules/legacy-tasks/tasks.types';

const dialogTitles = {
  add: 'Add Predecessor',
  edit: 'Edit Predecessor',
  delete: 'Remove Predecessor',
} as const;

export type ManageTaskPredecessorsMode = keyof typeof dialogTitles;

export interface ManageTaskPredecessorsData {
  rootTask?: Task;
  selectedPredecessor?: Predecessor;
  taskPredecessorOptions?: Task[];
  mode: ManageTaskPredecessorsMode;
}

@Component({
  selector: 'manage-task-predecessor',
  standalone: true,
  imports: [
    TsDialogComponent,
    TsButtonModule,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    TsSelectModule,
    AsyncPipe,
    TsSkeletonComponent,
    TsInputComponent,
  ],
  templateUrl: './manage-task-predecessor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageTaskPredecessorComponent implements OnInit {
  @Input() disabled: boolean;

  protected dialogRef = inject(DialogRef);
  protected dialogData = inject<ManageTaskPredecessorsData>(DIALOG_DATA);
  protected formBuilder = inject(FormBuilder);
  private tasksService = inject(TasksService);
  private cdr = inject(ChangeDetectorRef);

  protected predecessorForm = this.formBuilder.group<Record<keyof Predecessor, any>>({
    id: this.dialogData?.selectedPredecessor?.id || null,
    task_id: this.rootTask?.id,
    predecessor_task_id: [this.dialogData?.selectedPredecessor?.predecessor_task_id || null, Validators.required],
    predecessor_type: this.dialogData?.selectedPredecessor?.predecessor_type || 'FS',
    title: this.dialogData?.selectedPredecessor?.title || '',
    lag: this.dialogData?.selectedPredecessor?.lag || 0,
    lag_type: this.dialogData?.selectedPredecessor?.lag_type || 'business_days',
  });
  protected predecessorTypes = [...PREDECESSOR_TYPES];

  loadingTasks = false;

  get predecessor() {
    return this.dialogData?.selectedPredecessor;
  }

  get predecessorTitle(): string {
    return this.dialogData?.selectedPredecessor.title;
  }

  get rootTask(): Task {
    return this.dialogData?.rootTask;
  }

  get dialogTitle() {
    return dialogTitles[this.dialogData.mode];
  }

  get lagTypes() {
    const lagMap = {
      [DurationType.BusinessDays]: 'Business Days',
      [DurationType.CalendarDays]: 'Calendar Days',
    };
    return Object.keys(lagMap).map((type) => ({ type: type, label: lagMap[type] }));
  }

  get mode(): ManageTaskPredecessorsMode {
    return this.dialogData.mode;
  }

  get predecessorOptions(): Task[] {
    const { taskPredecessorOptions } = this.dialogData;

    if (!taskPredecessorOptions) return [];

    return taskPredecessorOptions.filter(
      (task) =>
        // remove the root/current task from the options
        task.id !== this.rootTask.id &&
        // remove from the options the tasks that are already predecessors of the root task...
        (!this.rootTask.predecessors.some((p) => p.predecessor_task_id === task.id) ||
          // ...but ensures that the selected predecessor is still in the list
          task.id === this.dialogData?.selectedPredecessor?.predecessor_task_id),
    );
  }

  ngOnInit(): void {
    if (!this.predecessorOptions?.length) {
      this.loadingTasks = true;
      this.tasksService.getTasks(this.rootTask.task_list_id).subscribe((tasks) => {
        this.dialogData.taskPredecessorOptions = TaskUtils.flattenTasks(tasks);
        this.loadingTasks = false;
        this.cdr.detectChanges();
      });
    }
  }

  onPredecessorDeleted() {
    const { selectedPredecessor } = this.dialogData;

    this.dialogRef.close(selectedPredecessor as Predecessor);
  }

  onPredecessorChanged() {
    if (this.predecessorForm.invalid) return;

    // When adding a new predecessor we still don't have a title, so we need to extract it from the selected task.
    if (this.predecessorForm.value.title === '') {
      this.predecessorForm
        .get('title')
        .setValue(
          this.extractPredecessorTitleFromTasks(
            this.predecessorOptions,
            <number>this.predecessorForm.value.predecessor_task_id,
          ),
          { emitEvent: false },
        );
    }

    this.dialogRef.close(this.predecessorForm.getRawValue() as Predecessor);
  }

  private extractPredecessorTitleFromTasks(predecessorTasks: Task[], predecessorTaskId: number): string {
    const foundTask = predecessorTasks.find((task) => task.id === predecessorTaskId);

    return foundTask?.title || '';
  }
}
